import * as d3 from "d3";
import Grapher from "grapher";
import _ from "underscore";

class Graph {
  static canCollapse() {
    return false;
  }

  static name(props) {
    return props.name;
  }

  static className() {
    return "histogram";
  }

  static dataName(_props) {
    return "histogram-visualization";
  }

  static graphId(_props) {
    return "histogram-props";
  }

  static alwaysShowName() {
    return true;
  }

  static printSize() {
    return 450;
  }

  constructor($svgContainer, props) {
    if (props.query === 'qcov') window.debugQcovHsp = []; // TODO: Remove me after NCBI fix the issue from their side
    this.maximumXAxis = null;
    this.setMaximumXAxis(props);

    this.color = props.color;
    this.queries = [];
    this.props = props;
    for (let query of props.queries) {
      if (query.hits.length < 1) continue;

      this.formatValues(query, props);
      this.queries.push(query);
    }
    this.svgContainer = $svgContainer;
    if (this.queries.length > 0) {
      this.initiate();
    }
  }

  setMaximumXAxis(props) {
    switch (props.query) {
      case "evalue":
        this.maximumXAxis = 180;
        break;
      case "qcov":
      case "qcov_per_subject":
      case "similarity":
        this.maximumXAxis = 100;
        break;
      case "hsps_per_hit":
        this.maximumXAxis = 10;
        break;
    }
  }

  formatValues(query, props) {
    var value = 0;
    query.hitValues = [];
    query.hits.forEach((hit) => {
      value = 0;
      switch (props.query) {
        case "evalue":
          query.hitValues = [
            ...query.hitValues,
            ...this.formatEvalues(hit.hsps),
          ];
          break;
        case "similarity":
          query.hitValues = [
            ...query.hitValues,
            ...this.formatSimilarities(hit.hsps),
          ];
          break;
        case 'qcov':
          query.hitValues = [
            ...query.hitValues,
            ...this.formatQcov(hit.hsps),
          ];
          break;
        case 'qcov_per_subject':
          value = parseInt(hit.qcovs) === 100 ? 99 : parseInt(hit.qcovs)
          query.hitValues.push(value);
          break;
        case "hsps_per_hit":
          var hsps_length = hit.hsps.length > 10 ? 10 : hit.hsps.length;
          query.hitValues.push(hsps_length - 1);
          break;
      }
    });
  }

  formatQcov(hsps) {
    let values = [];
    let qcovhsp = 0;
    for (let hsp of hsps) {
      qcovhsp = parseInt(hsp.qcovhsp);
      // TODO: Remove me after NCBI fix the issue from their side
      // temporary fix for issue from NCBI : https://github.com/wurmlab/sequenceserver-cloud/issues/406
      if (['blastx', 'tblastx'].includes(this.props.program)) {
        let hspRow = window.debugQcovHsp.find(val => val.original === qcovhsp && val.muliplied === (qcovhsp * 3));
        if (hspRow === undefined) {
          window.debugQcovHsp.push({original: qcovhsp, muliplied: qcovhsp * 3, count: 1});
        } else {
          hspRow.count += 1
        }

        qcovhsp *= 3
      }

      values.push(qcovhsp === 100 ? qcovhsp - 1 : qcovhsp);
    }

    return values;
  }

  formatSimilarities(hsps) {
    let values = [];
    for (let hsp of hsps) {
      let similarity = Math.round((hsp.positives / hsp.length) * 100);
      values.push(similarity === 100 ? similarity - 1 : similarity);
    }

    return values;
  }

  formatEvalues(hsps) {
    let evalues = [];
    for (let hsp of hsps) {
      let evalue = parseInt(String(hsp.evalue).split("e-")[1] || 180);
      evalues.push(evalue === 180 ? evalue - 1 : evalue);
    }

    return evalues;
  }

  initiate() {
    let values = this.queries.flatMap(query => query.hitValues);

    var margin = { top: 20, right: 10, bottom: 40, left: 40 },
      width = this.svgContainer.width() - margin.left - margin.right,
      height = 300 - margin.top - margin.bottom;

    var svg = d3
      .select(this.svgContainer[0])
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    var max = this.maximumXAxis || d3.max(values);
    if (d3.min(values) === max) max = max + 1;

    var x = d3.scaleLinear().domain([0, max]).range([0, width]);
    var data = d3.bin().domain(x.domain()).thresholds(x.ticks(max))(values);
    var y = d3.scaleLinear()
      .domain([0, d3.max(data, (d) => d.length)])
      .range([height, 0]);

    this.drawAxis(svg, x, y, height, width);
    this.drawBar(data, svg, x, y, height);
  }

  xAxisLegend() {
    switch(this.props.query) {
      case 'evalue':
        return 'E-Value (1e-X)'
      case 'similarity':
        return 'Matches/alignment length (%)'
      case 'hsps_per_hit':
        return 'Number of HSPs'
      case 'qcov':
      case 'qcov_per_subject':
        return 'Query Coverage (%)'
    }
  }

  drawAxis(svg, x, y, height, width) {
    this.drawYAxis(svg, y, height);

    var xTicksLen = this.props.query === 'hsps_per_hit' ? 11 : 10;
    var xAxis = d3.axisBottom(x).ticks(xTicksLen);
    if (this.props.query == "hsps_per_hit") {
      var binCenters = Array.from({length: xTicksLen}, (_, i) => i + 0.5)
      xAxis
        .tickValues(binCenters)
        .tickFormat(function (_d, i) {
          return i === 9 ? '≥ 10' : i + 1;
        })
        .ticks(xTicksLen);
    }

    svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(0," + height + ")")
      .call(xAxis);

    svg.append("text")
      .attr("x", width / 2) 
      .attr("y", height + 20)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(this.xAxisLegend()); // Replace with your label text
  }

  drawYAxis(svg, y, height) {
    var yAxisLegend = this.props.query === "qcov" ? "Number of HSPs" : "Number of hits";
    var yAxis = svg.append("g").attr("class", "y axis").call(d3.axisLeft(y));

    yAxis
      .select("path")
      .style({ stroke: "black", fill: "none", "stroke-width": "1px" });

    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", '-40')
      .attr("x", -height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(yAxisLegend);
  }

  drawBar(data, svg, x, y, height) {
    svg
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", 1)
      .attr("transform", function(d) { return "translate(" + x(d.x0) + "," + y(d.length) + ")"; })
      .attr("width", function(d) { return x(d.x1) - x(d.x0); })
      .attr("height", function(d) { return height - y(d.length); })
      .attr("fill", this.color);
  }
}

export default Grapher(Graph);
